import extractJWTPayload from '../utils/extractJWTPayload';
import isEqual from 'lodash/isEqual';
import isValidGuid from '../../util/isValidGuid';
import logger, { LogLevels } from '../../log';
import prependProtocol from '../utils/prependProtocol';
import ContentConfig, { ReportingContext } from '../../config/ContentConfig';
import cleanse from '../../config/util/cleanse';
import {
  Ecdn11Block,
  GeneralBlock,
  Report
} from '../../reporter/report/Report';
import { FailedRequest } from './isaFailedRequest';
import LocalState, { ReporterState } from '../../state/LocalState';
import { NOT_PEERABLE, Playback } from '../constants';
import mergeObjects from '../../util/mergeObjects';
import { mergeInInitialReport } from './report/updateInitialReport';

const log = logger.getLogger(__filename);
const MILLIS_IN_SECOND = 1000;

const method = 'POST';
type RSU = Record<string, unknown>;

type Body =
  | {
      contentId: string;
      tenantId: string;
      record: RSU;
      reportingContest: RSU;
      token: string;
    }
  | RSU;

export type KsdkRequest = {
  method: 'POST';
  body: Body;
  headers?: { Authorization?: string };
  url: string;
};

export type FormatReportRequest = KsdkRequest | FailedRequest;

export class ReportRequestData {
  appName: string;
  contentSet: boolean;
  headers: Record<string, string>;
  initialReport: Report;
  jwt?: string | undefined;
  report: Report;
  reportingContext: ReportingContext;
  state: ReporterState;
  localState: LocalState;
  isBeacon: boolean;

  constructor(obj?: ReportRequestData) {
    this.appName = (obj && obj.appName) || 'ksdk';
    this.contentSet = (obj && obj.contentSet) || false;
    this.headers = (obj && obj.headers) || {};
    this.initialReport = (obj && obj.initialReport) || new Report();
    this.jwt = obj && obj.jwt;
    this.report = (obj && obj.report) || new Report();
    this.state = (obj && obj.state) || ({} as ReporterState);
    this.localState = obj?.localState ?? ({} as LocalState);
    this.reportingContext = obj?.reportingContext as ReportingContext;
    this.isBeacon = obj?.isBeacon ?? true;
  }
}

export const updateWebrtcClusterIdFromTransfers = (
  report: Report,
  state: ReporterState
): void => {
  if (report.transfers && report.transfers.data.length > 0) {
    const transfers = report.transfers.data;
    const transfer = transfers[transfers.length - 1];
    const { clusterId } = transfer;
    if (isValidGuid(clusterId)) {
      // DONT SET THIS TO PENDING_CLUSTER !!!!
      state.activeClusterId = clusterId;
    }
  }
};

export const isValidGuidOrNotPeerable = (guid?: string): boolean => {
  return isValidGuid(guid) || guid == NOT_PEERABLE;
};

export const updateActiveClusterIds = (data: ReportRequestData): void => {
  const { report, state } = data;
  updateWebrtcClusterIdFromTransfers(report, state);

  const validWebrtc = isValidGuidOrNotPeerable(state.activeClusterId);
  const validAgent = isValidGuid(state.agentClusterId);
  const isAgentChanged = state.agentClusterId != state.oldAgentClusterId;
  const isWebrtcChanged = state.activeClusterId != state.oldActiveClusterId;
  const ecdn11: Ecdn11Block = {};
  if ((validAgent && isAgentChanged) || (validWebrtc && isWebrtcChanged)) {
    state.initialReportDataSent = false;
  }
  if (validWebrtc) {
    state.oldActiveClusterId = state.activeClusterId;
    ecdn11.webrtc = { activeClusterId: state.activeClusterId };
  }
  if (validAgent) {
    state.oldAgentClusterId = state.agentClusterId;
    ecdn11.agent = { activeClusterId: state.agentClusterId };
  }
  // SRV-4382: Always set ecdn11
  report.ecdn11 = new Ecdn11Block(mergeObjects([state.ecdn11, ecdn11], true));
};

export interface Build11Request {
  headers: Record<string, string>;
  config: ContentConfig;
  state: ReporterState;
  reportingContext: ReportingContext;
  record: Report;
  jwt: string;
  contentSet: boolean;
  isBeacon: boolean;
}

export const hasClusterIdForState = (args: Build11Request): boolean => {
  if (args.state.hasReporterTimedOut) {
    return true;
  }
  if (args.record.general.playback == Playback.ECDN11) {
    const clusterId = args.record.ecdn11?.agent?.activeClusterId;
    if (!args.config.peering.peeringDisabled) {
      return isValidGuid(clusterId);
    }
    return clusterId === NOT_PEERABLE;
  }
  if (args.record.general.playback == Playback.BBP) {
    return isValidGuid(args.record.ecdn11?.webrtc?.activeClusterId);
  }
  return true;
};

const checkFailureConditions = (
  args: Build11Request,
  jwtPayload: RSU
): FailedRequest | undefined => {
  const level = LogLevels.WARN;
  const expires = (jwtPayload.exp as number) * MILLIS_IN_SECOND;
  if (!hasClusterIdForState(args)) {
    return { status: 0, statusText: 'no-clusterId' };
  }
  if (expires - Date.now() <= 0) {
    return { status: 401, statusText: 'expired', level };
  }
  if (args.state.isReportingDisabled) {
    const status = args.state.reportStatus || 0;
    return { status, statusText: 'disabled', level };
  }
};

function build11Request(args: Build11Request): FormatReportRequest {
  const { headers, record, jwt } = args;
  const config = ContentConfig.getInstance();
  const jwtPayload = extractJWTPayload<RSU>(jwt) as RSU;

  const contentId = jwtPayload.cid;
  const tenantId = jwtPayload.tid;
  const failureReport = checkFailureConditions(args, jwtPayload);
  if (failureReport) {
    log.info('not sending report', args.record, failureReport);
    return failureReport;
  }
  const path = `/api/stats/play/${tenantId}/${contentId}`;
  const url = new URL(path, prependProtocol(config.sth)).toString();

  log.info('sending report', args.record);
  return {
    method,
    url,
    headers,
    body: cleanse({
      record,
      hasReportingContext: true,
      reportingContext: args.reportingContext,
      token: args.isBeacon ? jwt : undefined
    })
  };
}

export const addTagsIfAvailableAndDifferent = (
  tags: string[] | undefined,
  state: ReporterState,
  report: Report
): void => {
  if (!tags || tags.length == 0 || (state.tags && isEqual(tags, state.tags))) {
    return;
  }
  report.general.tags = tags;
  state.tags = tags;
};

export default function (data: ReportRequestData): FormatReportRequest {
  updateActiveClusterIds(data);
  const config = ContentConfig.getInstance();
  const requireConfig = !data.state.initialReportDataSent;
  if (!data.state.initialReportDataSent) {
    log.debug('Merging in required initial report data');
    mergeInInitialReport({ from: data.initialReport, into: data.report });
    // SRV-4263 - Last spot where this value could be improperly set. Send it
  }

  const { contentSet, headers, jwt, report, state, localState } = data;
  report.general.reportIndex = state.reportIndex;
  // SRV-1701: Set the appName with the latest value.
  if (report.general) {
    report.general.appName = data.appName;
  }

  const record: Report = cleanse(report);
  addTagsIfAvailableAndDifferent(config.tags, state, record);

  if (!jwt || !config.sth) {
    const msg = `JWT ${!!jwt} or stat host '${config.sth}' is missing`;
    return { level: LogLevels.WARN, statusText: msg, status: 0 };
  }
  // SRV-4382: Always set general fields.
  const general = {
    playType: localState.play.playType,
    start: localState.play.start,
    browserUserGuid: state.browserUserGuid,
    duration: state.duration
  } as GeneralBlock;
  record.general = mergeObjects([record.general, general], true);
  if (requireConfig) {
    record.general.config = config;
  }
  const args = {
    config,
    contentSet,
    headers,
    jwt,
    record,
    state,
    reportingContext: data.reportingContext,
    isBeacon: data.isBeacon
  };
  return build11Request(args);
}
